import type { ContentAttachment } from './valueObjects/contentAttachment';
import type { ContentImage } from './valueObjects/contentImage';
import type { ContentS3Path } from './valueObjects/contentS3Path';
import type { ContentText } from './valueObjects/contentText';
import type { ContentJson } from '@/types/domain/privateBlog/entities/contentEntity';

import { EntityPvt } from '@/domain/entityPvt';
import { EntityNamePvt } from '@/types/common/entityPvt';

/**
 * コンテンツプロパティ
 */
export type ContentProps = {
  /** ID */
  id: string,
  /** S3パス */
  s3Path: ContentS3Path | undefined,
  /** テキスト */
  text: ContentText,
  /** 画像リスト */
  images: ContentImage[],
  /** 添付リスト */
  attachments: ContentAttachment[],
}

/**
 * コンテンツEntity
 */
export class ContentEntity extends EntityPvt<
  ContentProps,
  ContentJson
> {
  /**
   * 画像の最大サイズ[px].
   */
  static get maxImageSize(): number {
    return 400;
  }

  /**
   * データJsonを返す
   * @returns データ
   */
  toJsonData(): ContentJson {
    const images = [];
    for (let i = 0; i < this.images.length; i += 1) {
      images.push(this.images[i].value);
    }
    const attachments = [];
    for (let i = 0; i < this.attachments.length; i += 1) {
      attachments.push({
        name: this.attachments[i].name,
        content: this.attachments[i].content,
      });
    }
    return {
      id: this.id,
      s3Path: this.s3Path?.value,
      text: this.text.value,
      images,
      attachments,
    };
  }

  /**
   * Entity名
   */
  get entityName(): EntityNamePvt {
    return EntityNamePvt.WagakoNurseryHomepageContentEntity;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return this.id;
  }

  /**
   * ID
   */
  get id(): string {
    return this._value.id;
  }

  /**
   * ID
   */
  set id(id: string) {
    this._value.id = id;
  }

  /**
   * S3パス
   */
  get s3Path(): ContentS3Path | undefined {
    return this._value.s3Path;
  }

  /**
   * S3パス
   */
  set s3Path(s3Path: ContentS3Path | undefined) {
    this._value.s3Path = s3Path;
  }

  /**
   * テキスト
   */
  get text(): ContentText {
    return this._value.text;
  }

  /**
   * テキスト
   */
  set text(text: ContentText) {
    this._value.text = text;
  }

  /**
   * 画像リスト
   */
  get images(): ContentImage[] {
    return this._value.images;
  }

  /**
   * 画像リスト
   */
  set images(images: ContentImage[]) {
    this._value.images = images;
  }

  /**
   * 添付リスト
   */
  get attachments(): ContentAttachment[] {
    return this._value.attachments;
  }

  /**
   * 添付リスト
   */
  set attachments(attachments: ContentAttachment[]) {
    this._value.attachments = attachments;
  }
}
